import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { usePremium } from '@/hooks/usePremium';
import { buyPremiumAndSync, restorePremiumAndSync } from '@/lib/premiumPurchase';
import {
  PREMIUM_PRODUCT_IDS,
  formatYearlyAsMonthlyPrice,
  getPremiumProducts,
  isPurchasesAvailable,
  type StoreProduct,
} from '@/lib/straivPurchases';
import { Analytics } from '@/lib/analytics';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional headline override (e.g. AI Run Coach) */
  title?: string;
  description?: string;
  /** Funnel attribution for Firebase */
  source?: string;
};

export function PremiumPaywall({ open, onOpenChange, title, description, source = 'unknown' }: Props) {
  const { t } = useTranslation();
  const { isPremium, refetchProfile } = usePremium();
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const trackedOpenRef = useRef(false);

  useEffect(() => {
    if (!open || !isPurchasesAvailable()) return;
    let cancelled = false;
    setLoadingProducts(true);
    void getPremiumProducts()
      .then((list) => {
        if (!cancelled) setProducts(list);
      })
      .catch((err) => {
        console.warn('[PremiumPaywall] products', err);
        if (!cancelled) toast.error(t('premium.paywallLoadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoadingProducts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  // Track view when opened directly (e.g. Settings / RunningStart), not via PremiumGate.
  useEffect(() => {
    if (open && !trackedOpenRef.current) {
      trackedOpenRef.current = true;
      Analytics.paywallViewed(source);
    }
    if (!open) trackedOpenRef.current = false;
  }, [open, source]);

  useEffect(() => {
    if (open && isPremium) onOpenChange(false);
  }, [open, isPremium, onOpenChange]);

  const monthly = products.find((p) => p.id === PREMIUM_PRODUCT_IDS.monthly);
  const yearly = products.find((p) => p.id === PREMIUM_PRODUCT_IDS.yearly);
  const yearlyPerMonth = formatYearlyAsMonthlyPrice(yearly);

  const onBuy = async (productId: string) => {
    setBusyId(productId);
    const selected = products.find((p) => p.id === productId);
    Analytics.purchaseStarted(productId, source, {
      value: selected?.price,
      currency: selected?.currencyCode,
      content_name: selected?.displayName,
    });
    try {
      const result = await buyPremiumAndSync(productId);
      if ('cancelled' in result && result.cancelled) {
        Analytics.purchaseCancelled(productId, source);
        return;
      }
      if ('pending' in result && result.pending) {
        Analytics.purchasePending(productId, source);
        toast.message(t('premium.paywallPending'));
        return;
      }
      if (result.premium) {
        Analytics.purchaseCompleted(productId, source, {
          expires_at: ('expiresAt' in result ? result.expiresAt : undefined) ?? undefined,
          value: selected?.price,
          currency: selected?.currencyCode,
          content_name: selected?.displayName,
        });
        Analytics.setPremiumStatus(true);
        await refetchProfile();
        toast.success(t('premium.paywallSuccess'));
        onOpenChange(false);
      } else {
        Analytics.purchaseFailed(productId, 'verify_failed', source);
        toast.error(('message' in result ? result.message : undefined) || t('premium.paywallVerifyFailed'));
      }
    } catch (err: any) {
      console.error('[PremiumPaywall] purchase', err);
      Analytics.purchaseFailed(productId, err?.message || 'purchase_error', source);
      toast.error(err?.message || t('premium.paywallPurchaseFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const onRestore = async () => {
    setBusyId('restore');
    try {
      const result = await restorePremiumAndSync();
      if (result.premium) {
        Analytics.purchaseRestored(true, source);
        Analytics.setPremiumStatus(true);
        await refetchProfile();
        toast.success(t('premium.paywallRestored'));
        onOpenChange(false);
      } else {
        Analytics.restoreEmpty(source);
        toast.message(result.message || t('premium.paywallNothingToRestore'));
      }
    } catch (err: any) {
      console.error('[PremiumPaywall] restore', err);
      Analytics.purchaseFailed('restore', err?.message || 'restore_error', source);
      toast.error(err?.message || t('premium.paywallRestoreFailed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && open) Analytics.paywallDismissed(source);
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader className="space-y-2 text-left">
          <DialogTitle className="text-xl">
            {title || t('premium.paywallTitle')}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {description || t('premium.paywallBody')}
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-2 text-sm text-foreground/90 my-2">
          <li className="flex gap-2 items-start"><span className="text-primary font-bold shrink-0">✓</span>{t('premium.paywallBenefit1')}</li>
          <li className="flex gap-2 items-start"><span className="text-primary font-bold shrink-0">✓</span>{t('premium.paywallBenefit2')}</li>
          <li className="flex gap-2 items-start"><span className="text-primary font-bold shrink-0">✓</span>{t('premium.paywallBenefit3')}</li>
          <li className="flex gap-2 items-start"><span className="text-primary font-bold shrink-0">✓</span>{t('premium.paywallBenefit4')}</li>
          <li className="flex gap-2 items-start"><span className="text-primary font-bold shrink-0">✓</span>{t('premium.paywallBenefit5')}</li>
        </ul>

        {!isPurchasesAvailable() ? (
          <p className="text-sm text-muted-foreground">{t('premium.paywallIosOnly')}</p>
        ) : loadingProducts ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('premium.paywallLoading')}
          </div>
        ) : (
          <div className="space-y-2">
            <Button
              className="w-full h-auto py-3 bg-gradient-primary text-black font-semibold flex flex-col gap-0.5"
              disabled={!!busyId || !yearly}
              onClick={() => void onBuy(PREMIUM_PRODUCT_IDS.yearly)}
            >
              {busyId === PREMIUM_PRODUCT_IDS.yearly ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <span>{t('premium.paywallYearly')}</span>
                  <span className="text-xs font-normal opacity-80">
                    {yearly?.displayPrice || '—'} · {t('premium.paywallBestValue')}
                  </span>
                  {yearlyPerMonth && (
                    <span className="text-[11px] font-normal opacity-70">
                      {t('premium.paywallYearlyPerMonth', { price: yearlyPerMonth })}
                    </span>
                  )}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              className="w-full h-auto py-3 flex flex-col gap-0.5"
              disabled={!!busyId || !monthly}
              onClick={() => void onBuy(PREMIUM_PRODUCT_IDS.monthly)}
            >
              {busyId === PREMIUM_PRODUCT_IDS.monthly ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <span>{t('premium.paywallMonthly')}</span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {t('premium.paywallMonthlyTrial', { price: monthly?.displayPrice || '—' })}
                  </span>
                </>
              )}
            </Button>
            <p className="text-[11px] leading-snug text-muted-foreground px-1 pt-1">
              {t('premium.paywallTrialNote')}
            </p>
            <Button
              variant="ghost"
              className="w-full text-sm"
              disabled={!!busyId}
              onClick={() => void onRestore()}
            >
              {busyId === 'restore' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('premium.paywallRestore')
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
